import { PromoVoucher, PromoVoucherType } from 'Components/DeliveryMethod';
import ModalDialog from 'Components/ModalDialog';
import { C2cOverPaymentTextEnum } from 'Pages/C2cOrderPayment/enums';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
	c2cOrderPaymentSelectors,
	getC2cVouchersAction,
	C2cVoucherModel,
	chooseVoucherAction,
	updateVoucherValidation,
} from 'Stores/c2c-order-payment';
import { orderBillSelectors } from 'Stores/order-bill';
import InputText from 'Components/InputText';
import ButtonClick from 'Components/ButtonClick';
import { CloseOutlined } from '@ant-design/icons';
import { classPrefix } from 'Utilities/global';
import './style.scss';
import InputMessage from 'Components/InputMessage';
import { VOUCHER_STATUS } from 'Utilities/constants';
import C2cOverPaymentServiceV1 from 'Services/v1/c2c-order-payment';
import { toLower } from 'lodash';

const VoucherIcon = () => (
	<svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg" className="promotion-title-icon">
		<path d="M1.0857e-08 8.4937C-2.66281e-05 8.35291 0.0489689 8.21645 0.138682 8.10743C0.228395 7.99842 0.353306 7.92357 0.492244 7.89556C1.0482 7.78276 1.54786 7.48273 1.9067 7.04622C2.26554 6.6097 2.46154 6.0635 2.46154 5.5C2.46154 4.9365 2.26554 4.3903 1.9067 3.95378C1.54786 3.51727 1.0482 3.21724 0.492244 3.10444C0.353306 3.07643 0.228395 3.00158 0.138682 2.89257C0.0489689 2.78355 -2.66281e-05 2.64709 1.0857e-08 2.5063V0.611111C1.0857e-08 0.449034 0.064835 0.293596 0.180242 0.17899C0.295649 0.0643849 0.452174 0 0.615385 0H15.3846C15.5478 0 15.7044 0.0643849 15.8198 0.17899C15.9352 0.293596 16 0.449034 16 0.611111V2.5063C16 2.64709 15.951 2.78355 15.8613 2.89257C15.7716 3.00158 15.6467 3.07644 15.5078 3.10444C14.9518 3.21724 14.4521 3.51727 14.0933 3.95379C13.7345 4.3903 13.5385 4.9365 13.5385 5.5C13.5385 6.0635 13.7345 6.60971 14.0933 7.04622C14.4521 7.48274 14.9518 7.78277 15.5078 7.89556C15.6467 7.92357 15.7716 7.99842 15.8613 8.10744C15.951 8.21645 16 8.35291 16 8.4937V10.3889C16 10.551 15.9352 10.7064 15.8198 10.821C15.7044 10.9356 15.5478 11 15.3846 11H0.615385C0.452174 11 0.295649 10.9356 0.180242 10.821C0.064835 10.7064 1.0857e-08 10.551 1.0857e-08 10.3889V8.4937Z" fill="currentColor"/>
		<path d="M5 0L5 11" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
	</svg>
);

interface PromotionModalProps {
	visible: boolean;
	onClose: () => void;
}

const PromotionModal = (props: PromotionModalProps) => {
	const { visible, onClose } = props;
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [selectedVoucher, setSelectedVoucher] = useState<C2cVoucherModel>();
	const [validateStatus, setValidateStatus] = useState({
		status: '',
		message: '',
	});
	const receiverAddress = useSelector(orderBillSelectors.get).receiverAddress;
	const senderAddress = useSelector(orderBillSelectors.get).senderAddress;
	const { vouchers , typeOrder} = useSelector(c2cOrderPaymentSelectors.get);
	const voucherValidationResult = useSelector(
		c2cOrderPaymentSelectors.get,
	).voucherValidation;

	const voucherSelected = useSelector(
		c2cOrderPaymentSelectors.get,
	).voucherSelected;

	useEffect(() => {
		if (visible) {
			voucherSelected?.code && setSelectedVoucher(voucherSelected);
		} else {
			setValidateStatus({
				status: '',
				message: '',
			});
			setSelectedVoucher({ code: '' });
		}
	}, [visible]);

	useEffect(() => {
		voucherSelected?.code && setSelectedVoucher(voucherSelected);
	}, [voucherSelected]);

	useEffect(() => {
		if (voucherValidationResult && voucherValidationResult.status) {
			setValidateStatus({
				status: voucherValidationResult.status,
				message: voucherValidationResult.message || '',
			});

			if (voucherValidationResult.status === 'success') {
				onClose && onClose();
			}
		}
	}, [voucherValidationResult]);

	useEffect(() => {
		if (senderAddress && receiverAddress) {
			const originId = [
				Number(senderAddress?.country),
				Number(senderAddress?.province),
				Number(senderAddress?.district),
				Number(senderAddress?.village),
			];
			const destinationId = [
				Number(receiverAddress.country),
				Number(receiverAddress.province),
				Number(receiverAddress.district),
				Number(receiverAddress.village),
			];
			if (originId.length && destinationId.length) {
				dispatch(
					getC2cVouchersAction({
						productId: 0,
						deliveryType: typeOrder,
						originId,
						destinationId,
					}),
				);
			}
		}
	}, [receiverAddress, senderAddress, typeOrder]);

	const chooseVoucher = (voucher: C2cVoucherModel) => {
		if (voucher.code) {
			setSelectedVoucher(voucher);
		}
	};

	const onClearValue = () => {
		setSelectedVoucher(undefined);
		setValidateStatus({
			status: '',
			message: '',
		});
	};

	const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
		setSelectedVoucher({ code: e.target.value });
		if (!e.target.value) {
			setValidateStatus({
				status: '',
				message: '',
			});
		}
	};

	const applyVoucher = async () => {
		if (!selectedVoucher?.code) return;

		setValidateStatus({
			status: 'checking',
			message: 'voucher_availability_validating',
		});

		const checkVoucherAvailabilityRes =
			await C2cOverPaymentServiceV1.checkVoucherAvailability({
				voucher: selectedVoucher?.code,
				delivery_type: typeOrder,
			});

		if (checkVoucherAvailabilityRes.error) {
			setValidateStatus({
				status: 'error',
				message: 'global_error_general',
			});
			return;
		}

		const voucherStatus = checkVoucherAvailabilityRes.data;
		if (voucherStatus !== VOUCHER_STATUS.VALID) {
			setValidateStatus({
				status: 'error',
				message: voucherStatus || '',
			});
			return;
		}

		setValidateStatus({
			status: 'success',
			message: 'voucher_message_apply_success',
		});
		return (
			dispatch(
				updateVoucherValidation({
					status: 'success',
					message: 'voucher_message_apply_success',
				}),
			) && dispatch(chooseVoucherAction(selectedVoucher))
		);
	};

	const renderVouchers = () => {
		if (!vouchers?.length) {
			return <></>;
		}
		return vouchers?.map((voucher, index) => {
			return (
				<div
					className="cursor-pointer mt-4"
					key={index}
					onClick={() => chooseVoucher(voucher)}
				>
					<PromoVoucher
						key={voucher?.id}
						voucherName={voucher?.tittle}
						voucherContent={voucher?.content}
						discountType={voucher?.discountType}
						discountAmount={voucher?.discountAmount}
						voucherType={PromoVoucherType.Voucher}
						endAt={voucher?.endAt}
						usageLimit={voucher?.remainingUsagePerCustomer || voucher?.usageLimit}
					></PromoVoucher>
				</div>
			);
		});
	};

	const compClass = `${classPrefix}-promotion-page`;

	return (
		<ModalDialog
			visible={visible}
			title={
				<>
					<VoucherIcon />
					<span className="ps-2">{t('menu_item_your_deals')}</span>
				</>
			}
			hideFooter={true}
			onHandleCancel={onClose}
			destroyOnClose={true}
		>
			<div className={compClass}>
				<div className="d-flex">
					<InputText
						className="flex-fill w-100"
						placeholder={t(
							C2cOverPaymentTextEnum.C2cPromo_PlaceHolderInput,
						)}
						value={selectedVoucher?.code}
						onChange={onChangeValue}
						suffix={
							<CloseOutlined
								className={`${
									selectedVoucher?.code ? '' : 'd-none'
								} cursor-point`}
								onClick={onClearValue}
							/>
						}
					/>
					<ButtonClick
						variant="primary"
						onClick={applyVoucher}
						disabled={!selectedVoucher?.code}
						className="ms-2"
						containerClassName={'flex-basis-0'}
					>
						{t(C2cOverPaymentTextEnum.C2cPromoCode_BtnApply)}
					</ButtonClick>
				</div>
				<div className={'mt-2'}>
					{(validateStatus.status === 'error' ||
						validateStatus.status === 'checking') && (
						<InputMessage
							message={t(validateStatus.message || '')}
							type={
								validateStatus.status === 'checking'
									? 'info'
									: validateStatus.status
							}
						/>
					)}
				</div>
				{renderVouchers()}
			</div>
		</ModalDialog>
	);
};

export default PromotionModal;
